import { Http, HttpHeaders } from '@capacitor-community/http';
import { Preferences } from '@capacitor/preferences';
import MainScene from 'src/app/Scenes/MainScene';
import UIScene from './UI/UIScene';
import { GlobalService } from 'src/app/services/global.service';

// getPlatforms(); // returns ["iphone", "ios", "mobile", "mobileweb"] from an iPhone

export enum weatherStates {
  sunny = 'sunny',
  cloudy = 'cloudy',
  rainy = 'rainy',
  thunderstorm = 'thunderstorm',
  snow = 'snow',
}

export enum connectionStatus {
  active,
  inactive,
  error,
}

const weatherSynonyms: Map<string, Array<string>> = new Map([
  [weatherStates.sunny, ['sun']],
  [weatherStates.cloudy, ['cloud', 'overcast']],
  [weatherStates.rainy, ['rain', 'shower']],
  [weatherStates.thunderstorm, ['thunder', 'lightening']],
  [weatherStates.snow, ['snow', 'sleet', 'ice', 'flurries', 'flurry']],
]);

export class WeatherService {
  // Note: NOAA requires a User-Agent header for authentication or request will be denied.
  static headers: HttpHeaders = {
    'User-Agent': '(NorthBankSystems-SmallTown, cannellajs2@vcu.edu)',
  };
  public static rvaHourlyForecastURL: string =
    'https://api.weather.gov/gridpoints/AKQ/44,78/forecast/hourly';
  // TODO: Pull coords from GPS
  // NOTE: you will need to get grid coords from a lat lon
  // Use this: https://api.weather.gov/points/37.57,-77.47
  public static weatherConnectivity: number = connectionStatus.inactive;
  static weatherState: string = weatherStates.sunny;

  // Define the particle emission region.
  static particleEmissionRegion;

  // Weather particle simulation objects
  static cloudEmitter: Phaser.GameObjects.Particles.ParticleEmitter;
  static raindropEmitter: Phaser.GameObjects.Particles.ParticleEmitter;
  static snowflakeEmitter: Phaser.GameObjects.Particles.ParticleEmitter;
  static mainScene: MainScene;
  static lighteningTimeout: NodeJS.Timeout;

  public static get(performGet: boolean) {
    if (performGet) {
      console.log('Requesting Weather Data');
      Http.request({
        url: WeatherService.rvaHourlyForecastURL,
        method: 'GET',
        headers: WeatherService.headers,
      })
        .then(async (response) => {
          console.log('Received a weather response:');
          if (response.status === 200) {
            const data = await response.data;
            console.log('Received Weather Data:');
            const hourlyForecastPeriods: any = JSON.parse(data).properties.periods;
            console.log(hourlyForecastPeriods);
            const currentWeather: string = hourlyForecastPeriods[0].shortForecast;
            console.log('Current Weather');
            console.log(currentWeather);

            // Set the current weather state to the most recent forecast
            WeatherService.weatherState = WeatherService.forecastParser(currentWeather);

            // Set the weather connectivity after the state
            WeatherService.weatherConnectivity = connectionStatus.active;
            if (GlobalService.$weatherConnectivityState.value !== GlobalService.modeState.On){
              GlobalService.$weatherConnectivityState.next(GlobalService.modeState.On);
            }
            console.log(`weatherState: ${WeatherService.weatherState}`);
            UIScene.showIconBasedOnConnectivity(GlobalService.$weatherConnectivityState.value, WeatherService.weatherState);
            WeatherService.setAppropriateWeatherSimulation();
            await Preferences.set({
              key: 'hourlyForecast',
              value: hourlyForecastPeriods,
            });
          }
        })
        .catch((e) => {
          console.error('Error retrieving weather request!');
          console.log(e);
          WeatherService.weatherConnectivity = connectionStatus.error;
          if(GlobalService.$weatherConnectivityState.value !== GlobalService.modeState.Off){
            GlobalService.$weatherConnectivityState.next(GlobalService.modeState.Off);
          }
          UIScene.showIconBasedOnConnectivity(GlobalService.$weatherConnectivityState.value, WeatherService.weatherState);
          WeatherService.setAppropriateWeatherSimulation();
        });
    } else {
      console.log('Data is not being requested');
      // Set the weather connectivity after the state
      WeatherService.weatherConnectivity = connectionStatus.inactive;
      
      if(GlobalService.$weatherConnectivityState.value !== GlobalService.modeState.Off){
        GlobalService.$weatherConnectivityState.next(GlobalService.modeState.Off);
      }
      UIScene.showIconBasedOnConnectivity(GlobalService.$weatherConnectivityState.value, WeatherService.weatherState);
      WeatherService.setAppropriateWeatherSimulation();
    }
  }

  // Select the most severe matching weather type
  static forecastParser(shortForecast: string) {
    const lowerCaseForecast = shortForecast.toLowerCase();

    // assign the default to be sunny
    let weatherState: string = weatherStates.sunny;
    weatherSynonyms.forEach((synonyms, key) => {
      synonyms.forEach((synonym) => {
        if (lowerCaseForecast.includes(synonym)) weatherState = key;
      });
    });
    return weatherState;
  }

  static preload(scene: MainScene) {
    WeatherService.mainScene = scene;
    scene.load.image('raindrop', 'assets/weather/raindrop.png');
    scene.load.image('snowflake', 'assets/weather/snowflake.png');
    scene.load.image('cloud', 'assets/weather/cloud-small-black.png');
  }

  static create(scene: MainScene) {
    // Setup simulations
    WeatherService.createWeatherSimulations(scene);

    // setup callbacks
    GlobalService.$weatherConnectivityState.subscribe({
      next: async state => {
        try{
          WeatherService.get(true);
        }
        catch(e){
          console.error("Unable to retrieve weather data!");
        }
      },
      error : error => {
        console.error('There was an error retrieving weather mode!', error);
        
      }
    });

    // Setup screen resize callbacks.
    scene.scale.on('resize', () => {
      WeatherService.onResize();
    });
  }

  static onResize(){
    WeatherService.particleEmissionRegion = new Phaser.Geom.Rectangle(
      WeatherService.mainScene.cameras.main.x - window.innerWidth*1.5,
      WeatherService.mainScene.cameras.main.y - window.innerHeight*1.5,
      2*window.innerWidth,
      2*window.innerHeight
    );
    WeatherService.cloudEmitter.emitZones[0].source = WeatherService.particleEmissionRegion;
    WeatherService.raindropEmitter.emitZones[0].source = WeatherService.particleEmissionRegion;
    WeatherService.snowflakeEmitter.emitZones[0].source = WeatherService.particleEmissionRegion;
  
  }

  static createWeatherSimulations(scene: MainScene) {
    console.log('createWeatherSimulations called!');
    const mainCamera = scene.cameras.main;

    // Define region for generating particles from
    WeatherService.particleEmissionRegion = new Phaser.Geom.Rectangle(
      mainCamera.x - window.innerWidth*1.5,
      mainCamera.y - window.innerHeight*1.5,
      2*window.innerWidth,
      2*window.innerHeight
    );

    // Setup cloud emitter
    WeatherService.cloudEmitter = scene.add.particles(
      mainCamera.x,
      mainCamera.y,
      'cloud',
      {
        // emit zone
        // Note: source is different for clouds
        emitZone: {
          source: new Phaser.Geom.Rectangle(
            mainCamera.x - mainCamera.width / 2,
            mainCamera.y - mainCamera.height / 2,
            mainCamera.width,
            -mainCamera.width / 4
          ),
          type: 'random',
          quantity: 0,
        },
        speedY: 25,
        speedX: { min: -20, max: 20 },

        // lifespan
        lifespan: 30000,

        scale: { min: 1.5, max: 3 },
        alpha: { min: 0.1, max: 0.25 },
        // tint: 0x000000,
        frequency: 15000,
        blendMode: 'MULTIPLY',

        // follow the player
        follow: scene.player,
      }
    );
    // WeatherService.cloudParticle.setDepth(2);
    WeatherService.cloudEmitter.setDepth(2);
    WeatherService.cloudEmitter.pause();

    // Setup raindrop emitter
    WeatherService.raindropEmitter = scene.add.particles(
      mainCamera.x,
      mainCamera.y,
      'raindrop',
      {
        // emit zone
        emitZone: {
          source: WeatherService.particleEmissionRegion,
          type: 'random',
          quantity: 0.4,
        },

        speedY: { min: 250, max: 300 },
        speedX: { min: -20, max: 20 },
        accelerationY: { min: 10, max: 15 },

        // lifespan
        lifespan: { min: 200, max: 500 },

        scale: { start: 0.12, end: 0.05 },
        alpha: { min: 0.7, max: 1 },
        gravityY: 50,
        frequency: 10,
        blendMode: 'ADD',

        // follow the player
        follow: scene.player,
      }
    );
    WeatherService.raindropEmitter.setDepth(2);
    WeatherService.raindropEmitter.pause();

    // Setup snowflake emitter
    WeatherService.snowflakeEmitter = scene.add.particles(
      mainCamera.x,
      mainCamera.y,
      'snowflake',
      {
        // emit zone
        emitZone: {
          source: WeatherService.particleEmissionRegion,
          type: 'random',
          quantity: 0.2,
        },
        speedY: { min: 50, max: 70 },
        speedX: { min: -20, max: 20 },
        accelerationY: { min: 10, max: 15 },

        // lifespan
        lifespan: { min: 500, max: 2000 },

        scale: { start: 0.15, end: 0.05 },
        alpha: { min: 0.7, max: 1 },
        gravityY: 10,
        frequency: 20,
        blendMode: 'ADD',

        // follow the player
        follow: scene.player,
      }
    );
    WeatherService.snowflakeEmitter.setDepth(2);
    WeatherService.snowflakeEmitter.pause();
  }

  static simulateLightening(runSimulation: boolean = true, meanTimeBetweenStrikesS: number = 40) {
    const camera: Phaser.Cameras.Scene2D.Camera =
      WeatherService.mainScene.cameras.main;
    if(runSimulation){
      this.lighteningCallback(
        camera,
        1,
        meanTimeBetweenStrikesS * 1000,
        (1000 * meanTimeBetweenStrikesS) / 4
      );
    }
    else if(WeatherService.lighteningTimeout){
      clearTimeout(WeatherService.lighteningTimeout);
    }
  }

  static lighteningCallback(
    camera: Phaser.Cameras.Scene2D.Camera,
    flashState: number,
    meanTimeBetweenStrikes: number,
    stdBetweenStrikesS: number
  ) {
    // Wait for the current strike to complete before starting another
    if (
      flashState === 1 &&
      WeatherService.weatherState === weatherStates.thunderstorm &&
      WeatherService.weatherConnectivity === connectionStatus.active
    ) {
      console.log('Flash!!!');
      const timeOffset: number = stdBetweenStrikesS * (2 * Math.random() - 1);
      const flashDuration: number = 250 + 100 * (2 * Math.random() - 1);
      WeatherService.lighteningTimeout = setTimeout(() => {
        camera.flash(
          flashDuration,
          255,
          255,
          255,
          false,
          (cam: Phaser.Cameras.Scene2D.Camera, state: number) => {
            this.lighteningCallback(
              cam,
              state,
              meanTimeBetweenStrikes,
              stdBetweenStrikesS
            );
          }
        );
      }, meanTimeBetweenStrikes + timeOffset);
    }
  }

  static setWeatherSimulation(weatherType: string, active: boolean) {
    // Determine which particle and emitter we should be toggling (Note: sunny is default, no emitters)
    let activeEmitters: Array<Phaser.GameObjects.Particles.ParticleEmitter> =
      [];
    if (
      weatherType === weatherStates.cloudy ||
      weatherType === weatherStates.rainy ||
      weatherType === weatherStates.thunderstorm
    ) {
      activeEmitters.push(WeatherService.cloudEmitter);
    }
    if (
      weatherType === weatherStates.rainy ||
      weatherType === weatherStates.thunderstorm
    ) {
      activeEmitters.push(WeatherService.raindropEmitter);
    }
    if (weatherType === weatherStates.snow) {
      activeEmitters.push(WeatherService.snowflakeEmitter);
    }

    // Generate lightening bolts if there are thunderstorms and weather mode is active
    WeatherService.simulateLightening(active && weatherType === weatherStates.thunderstorm);

    // Activate or deactivate the appropriate emitters
    if (active) {
      activeEmitters.forEach((e) => {
        e.setVisible(true);
        e.resume();
      });
    } else {
      activeEmitters.forEach((e) => {
        e.setVisible(false);
        e.pause();
      });
    }
  }

  static setAppropriateWeatherSimulation() {
    const isActive: boolean =
      WeatherService.weatherConnectivity === connectionStatus.active;
    WeatherService.setWeatherSimulation(WeatherService.weatherState, isActive);
  }
}